import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { LanguageSwitcher } from './LanguageSwitcher';

const navItems = [
  { key: 'navigation.menu.home', path: '/' },
  { key: 'navigation.menu.ourStory', path: '/about' },
  { key: 'navigation.menu.services', path: '/services' },
  { key: 'navigation.menu.howItWorks', path: '/how-it-works' },
];

export function Navigation() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleNavClick = (item: typeof navItems[0]) => {
    setIsMobileMenuOpen(false);
    navigate(item.path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleLogoClick = () => {
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleBookingClick = () => {
    navigate('/booking', { state: { isPaidSession: false } });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <motion.nav
      initial={{ opacity: 0, y: -20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-[#0A1A33]/95 backdrop-blur-md shadow-lg'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-[1280px] mx-auto px-6">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <motion.div
            whileHover={{ scale: 1.02 }}
            className="flex items-center space-x-2 cursor-pointer"
            onClick={handleLogoClick}
          >
            <div className="w-12 h-12 relative">
              <img 
                src="/logo.svg" 
                alt="Auren Stratus Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <div className="text-white font-semibold tracking-tight">
                Auren Stratus
              </div>
            </div>
          </motion.div>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center space-x-8">
            {navItems.map((item) => (
              <button
                key={item.path}
                onClick={() => handleNavClick(item)}
                className="group relative py-2"
              >
                <div className="text-white/90 group-hover:text-[#C6A667] transition-colors duration-300 font-medium">
                  {t(item.key)}
                </div>
                {/* Animated underline */}
                <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-0 h-0.5 bg-[#C6A667] group-hover:w-full transition-all duration-300" />
              </button>
            ))}
          </div>

          {/* Right side buttons */}
          <div className="hidden lg:flex items-center space-x-4">
            {/* Language Switcher */}
            <LanguageSwitcher />

            {/* CTA Button - Hide on booking page */}
            {location.pathname !== '/booking' && (
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
                className="px-6 py-3 bg-gradient-to-r from-[#C6A667] to-[#C6A667]/90 text-white rounded-full font-medium shadow-lg hover:shadow-xl transition-all duration-300"
                onClick={handleBookingClick}
              >
                {t('navigation.cta.consultation')}
              </motion.button>
            )}
          </div>

          {/* Mobile Right Side - Language Switcher + Menu Toggle */}
          <div className="lg:hidden flex items-center space-x-3">
            {/* Language Switcher for Mobile */}
            <LanguageSwitcher />
            
            {/* Mobile Menu Toggle */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-white hover:text-[#C6A667] transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            className="lg:hidden bg-[#0A1A33]/98 backdrop-blur-md border-t border-white/10"
          >
            <div className="max-w-[1280px] mx-auto px-6 py-6 space-y-4">
              {navItems.map((item, index) => (
                <motion.button
                  key={item.path}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => handleNavClick(item)}
                  className="block w-full text-left py-3 px-4 rounded-lg hover:bg-white/5 transition-colors group"
                >
                  <div className="text-white group-hover:text-[#C6A667] font-medium">
                    {t(item.key)}
                  </div>
                </motion.button>
              ))}

              {/* Hide booking button on booking page */}
              {location.pathname !== '/booking' && (
                <motion.button
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: navItems.length * 0.1 }}
                  className="w-full px-6 py-4 bg-gradient-to-r from-[#C6A667] to-[#C6A667]/90 text-white rounded-full font-medium shadow-lg text-center"
                  onClick={handleBookingClick}
                >
                  {t('navigation.cta.consultation')}
                </motion.button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
}
